/** @jsx isolateComponent */
import { useMemo, useState } from 'react';
import { css } from '@emotion/core';

import { isolateComponent } from '@zapier/style-encapsulation';
import { Icon } from '@zapier/zinnia-icons';
import { Colors, Text, Typography } from '@zapier/design-system';
import { ServiceIconShell } from '@zapier/design-system-patterns';

import { ServiceIcon } from '../../shared/ServiceIcon';
import ExternalLink from '../../shared/ExternalLink';

import { Incident as IncidentType } from './types';
import { groupIncidents } from './utils';

type IncidentsProps = {
  /** Displayed in the yellow header. */
  title: string;
  /** A list of incidents. */
  incidents: IncidentType[];
};

type IncidentProps = IncidentType & {
  showIcon: boolean;
};

const headingCss = css`
  background-color: ${Colors.StatusWarning};
  height: 58px;
  display: grid;
  grid-column-gap: 19px;
  grid-template-columns: min-content 1fr min-content;
  align-items: center;
  padding-left: 17px;
  padding-right: 30px;
  ${Typography.SmallPrint3}
  color: ${Colors.PrimeBlack};
`;

const toggleCss = css`
  white-space: nowrap;
  &:hover {
    color: ${Colors.UiPrimaryStrongest};
    text-decoration: underline;
  }
`;

const listCss = css`
  padding: 21px 55px 18px 13px;
  display: grid;
  grid-row-gap: 20px;
`;

const rowCss = css`
  padding-left: 13px;
  display: grid;
  grid-column-gap: 16px;
  grid-template-columns: min-content 1fr;
`;

const maybeIconCss = css`
  padding-top: 4px;
  width: 30px;
`;

const incidentInfoCss = css`
  display: grid;
  grid-row-gap: 5px;
`;

const linkCss = css`
  ${Typography.SmallPrint3}
  text-decoration: underline;
  color: ${Colors.UiPrimaryStronger};
  &:hover {
    color: ${Colors.UiBrandStrongest};
  }
`;

const Incident = ({
  showIcon,
  iconApp,
  status,
  name,
  body,
  shortlink,
}: IncidentProps) => {
  return (
    <li css={rowCss}>
      <div css={maybeIconCss}>
        {showIcon &&
          (iconApp ? (
            <ServiceIcon serviceName={iconApp} size={30} />
          ) : (
            <ServiceIconShell size={30}>
              <Icon color="StatusError" name="miscBoltOutlined" size={24} />
            </ServiceIconShell>
          ))}
      </div>
      <div css={incidentInfoCss}>
        <Text color="PrimeBlack" type="SmallPrint3">
          {status[0].toUpperCase()}
          {status.slice(1)}: {name}
        </Text>
        <Text color="GrayWarm6">{body}</Text>
        {shortlink && (
          <ExternalLink css={linkCss} href={shortlink}>
            View all updates
          </ExternalLink>
        )}
      </div>
    </li>
  );
};

/** Renders an expandable block with incidents. */
const Incidents = ({ title, incidents }: IncidentsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const groups = useMemo(() => groupIncidents(incidents), [incidents]);

  const toggleIsOpen = () => setIsOpen(!isOpen);
  return (
    <div>
      <div css={headingCss}>
        <Icon name="alertInfoFill" size={24} />
        <span>{title}</span>
        <button css={toggleCss} onClick={toggleIsOpen}>
          {isOpen ? 'Hide' : 'Learn more'}
        </button>
      </div>
      {isOpen && (
        <ol css={listCss}>
          {groups.map((groupOfIncidents, g) =>
            groupOfIncidents.map((incident, i) => (
              <Incident key={`${g}-${i}`} showIcon={i === 0} {...incident} />
            ))
          )}
        </ol>
      )}
    </div>
  );
};

export default Incidents;
